import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';

const LoginPage = () => {
  const { login } = useAuth();
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(loginEmail, loginPassword);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Tillytics Platform Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0f2b2b] p-12 flex-col justify-center items-center relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-8 left-8 w-4 h-4 border border-teal-400" style={{transform: 'rotate(45deg)'}}></div>
          <div className="absolute top-8 right-8 w-6 h-6 rounded-full border border-teal-400"></div>
          <div className="absolute bottom-8 left-8 w-4 h-4 border border-teal-400" style={{transform: 'rotate(45deg)'}}></div>
          <div className="absolute bottom-8 right-8 w-4 h-4 border border-teal-400" style={{transform: 'rotate(45deg)'}}></div>
        </div>
        
        {/* Tillytics Logo */}
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/681723f4-949e-4dfb-a708-ae22870c487f/images/8af9c8e44b25d7d46800cf9efdba1b8eebf9e58c62c7f9e0f73db0d39272f89d.png"
          alt="Tillytics"
          className="max-w-xs w-full h-auto"
        />
        <h1 className="text-4xl font-bold text-white mt-6 tracking-tight">Tillytics</h1>
        <p className="text-teal-300/70 text-center mt-4 max-w-sm text-lg">
          Till Analytics for Hospitality
        </p>
        <p className="text-teal-400/50 text-center mt-2 max-w-sm text-sm">
          Simple P&L reporting and business intelligence for cafes and restaurants
        </p>
      </div>

      {/* Right side - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/681723f4-949e-4dfb-a708-ae22870c487f/images/fc4c8d5b222cdc512f6e5f2e5185c2475b7c2e672eff6498f61984fab79c4fbe.png"
              alt="Tillytics"
              className="w-10 h-10 object-contain"
            />
            <span className="text-2xl font-bold font-heading">Tillytics</span>
          </div>

          <Card className="border-0 shadow-none lg:shadow-sm lg:border">
            <CardHeader className="space-y-1 px-0 lg:px-6">
              <CardTitle className="text-2xl font-bold">Welcome back</CardTitle>
              <CardDescription>
                Sign in to your account
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0 lg:px-6">
              <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="you@example.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type="password"
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                  />
                </div>
                {error && (
                  <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>
                )}
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
                <p className="text-xs text-center text-muted-foreground pt-2" data-testid="invite-hint">
                  New to Tillytics? <a href="/onboarding" className="text-primary hover:underline font-medium">Enter your invite code</a>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
      {process.env.REACT_APP_VERSION && (
        <span className="fixed bottom-3 right-4 text-[10px] text-muted-foreground/40 select-none" data-testid="app-version">
          v{process.env.REACT_APP_VERSION}
        </span>
      )}
    </div>
  );
};

export default LoginPage;
